// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import React, {
    useEffect,
    useState
} from 'react';
import clsx from 'clsx';
import {
    ComboBox,
    IComboBoxStyles,
    Icon,
    IDropdownOption,
} from 'office-ui-fabric-react';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import { useTranslation } from 'react-i18next';

import colors from './../../../styles/_colors.scss';
import { postalCodeValidation } from './../../../helpers/postalCodePatternPerCountry';
import { provinceValidation } from './../../../helpers/provinceRequiredPerCountry';
import Styles from './address.module.scss';
import { userAgentTierSelector } from './../../../store/user';
import { useSelector } from 'react-redux';
import isVatIDRequired from 'src/lib/validateVatID';

interface AddressFieldsProps {
    updateFields?: any;
    isFullDeviceEligible?: boolean;
    setCountryOnChange?: any;
    isCountryEnable?: boolean;
    readonly?: boolean;
    disabled: boolean;
    address1?: string;
    address2?: string;
    city?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    language?: string;
    phoneNumber?: string;
    postalCode?: string;
    country: string;
    stateOrProvince?: string;
    languageOptions: IDropdownOption[];
    countryOptions: IDropdownOption[];
    provincesOptions: IDropdownOption[];
    isWalkInCenter?: boolean;
    onAddressInputChanges?: (inputData: any) => void;
    isEnterpriseCustomer: boolean;
    vatId: string;
    customerPO: string;
    validation: {
        email?: string;
        firstName?: string;
        lastName?: string;
        phoneNumber?: string;
        address1?: string;
        address2?: string;
        city?: string;
        country?: string;
        postalCode?: string;
        stateOrProvince?: string
        secondaryContactName?: string
        secondaryContactPhoneNumber?: string
        vatId?: string
    };
    isOnSiteRepair: boolean;
    secondaryContactName: string;
    secondaryContactPhoneNumber: string;
    secondSRCreation: boolean;
    zipCode?: string;
    toDisplayIcon?: boolean;
    countryShortCode?: string;
}

const dropdownStyles: Partial<IComboBoxStyles> = {
    container: {
        minWidth: 100,
    },
    errorMessage: {
        fontSize: 12,
    },
    optionsContainerWrapper: {
        maxHeight: 350,
    },
};

const MAX_CHARACTER_LIMIT = 35;
export default function AddressFields(props: AddressFieldsProps) {
    const { t } = useTranslation();

    dropdownStyles.label = {
        color: props.readonly ? colors.waikawaGray : colors.black,
    };
    const [addressState, setAddressState] = useState(
        {
            firstName: props.firstName,
            lastName: props.lastName,
            phoneNumber: props.phoneNumber,
            language: props.language,
            postalCode: props.postalCode,
            address1: props.address1,
            address2: props.address2,
            email: props.email,
            city: props.city,
            country: props.country,
            stateOrProvince: props.stateOrProvince,
            vatId: props.vatId,
            customerPO: props.customerPO,
            validation: {},
            secondaryContactName: props.secondaryContactName,
            secondaryContactPhoneNumber: props.secondaryContactPhoneNumber,
            countryShortCode: props.countryShortCode
        }
    );

    //this will watch if the props from the parent has been changed=> rerender
    useEffect(() => {
        setAddressState({
            ...addressState,
            email: props.email,
            firstName: props.firstName,
            lastName: props.lastName,
            phoneNumber: props.phoneNumber,
            language: props.language,
            address1: props.address1,
            address2: props.address2,
            postalCode: props.postalCode,
            city: props.city,
            country: props.country,
            stateOrProvince: props.stateOrProvince,
            vatId: props.vatId,
            customerPO: props.customerPO,
            secondaryContactName: props.secondaryContactName,
            secondaryContactPhoneNumber: props.secondaryContactPhoneNumber,
            countryShortCode: props.countryShortCode
        });

    }, [
        props.email,
        props.firstName,
        props.lastName,
        props.phoneNumber,
        props.language,
        props.address1,
        props.address2,
        props.postalCode,
        props.city,
        props.country,
        props.stateOrProvince,
        props.vatId,
        props.customerPO,
        props.validation,
        props.secondaryContactName,
        props.secondaryContactPhoneNumber,
        props.countryShortCode
    ]);

    //will tell the parent whenever each field changes with a callback
    useEffect(() => {
        if (props.isCountryEnable) {
            props.onAddressInputChanges && props.onAddressInputChanges(addressState);

        }
    },
        [
            addressState.email,
            addressState.firstName,
            addressState.lastName,
            addressState.language,
            addressState.phoneNumber,
            addressState.address1,
            addressState.address2,
            addressState.postalCode,
            addressState.city,
            addressState.country,
            addressState.stateOrProvince,
            addressState.vatId,
            addressState.customerPO,
            addressState.validation,
            addressState.secondaryContactName,
            addressState.secondaryContactPhoneNumber,
        ]);

    const userAgentTier = useSelector(state => userAgentTierSelector(state));

    function handleAddressChange(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) {
        const { name } = event.target as HTMLInputElement;
        if (!props.isCountryEnable) {
            props.updateFields({ [name]: newValue });
        }
        setAddressState({ ...addressState, [name]: newValue });
    }
    function languageOnChange(event: any, selectedOption?: IDropdownOption) {
        if (selectedOption) {
            if (!props.isCountryEnable) {
                props.updateFields({ language: selectedOption.key });
            }
            setAddressState({ ...addressState, ['language']: selectedOption.key as unknown as string });
        }
    }

    function countryOnChange(event: any, selectedOption?: IDropdownOption) {
        if (selectedOption) {
            const selectedCountry = selectedOption.key as unknown as string;
            if (!props.isCountryEnable) {
                props.updateFields({ country: selectedCountry, stateOrProvince: '', city: '', postalCode: '' });
            }

            setAddressState({ ...addressState, country: selectedCountry, stateOrProvince: '', city: '', postalCode: '' });
        }
    }

    function provinceOnChange(event: any, selectedOption?: IDropdownOption) {
        if (selectedOption) {
            if (!props.isCountryEnable) {
                props.updateFields({ stateOrProvince: selectedOption.key });
            }
            setAddressState({ ...addressState, ['stateOrProvince']: selectedOption.key as unknown as string });
        }
    }

    const renderLabels = (labelProps: any, iconName: string, iconTitle: string, isRequired: boolean) => {
        if (labelProps.label === t('first_name_org_label') || labelProps.label === t('first_name') || labelProps.label === t('last_name_org_label') || labelProps.label === t('last_name')) {
            iconTitle = t('contact_icon_message', { fieldName: labelProps.label });
        }
        return (
            <span className={labelProps.disabled ? clsx(Styles.problemDescLabel, Styles.problemDescLabelDisabled) : Styles.problemDescLabel} >
                <span className={Styles.fieldName}>{labelProps.label}</span>
                {isRequired && <span className={Styles.problemDescRequired}> *</span>}
                {props.toDisplayIcon && <Icon iconName={iconName} title={iconTitle} className={labelProps.disabled ? clsx(Styles.problemDescIcon, Styles.fieldIconsDisabled) : Styles.problemDescIcon} />}
            </span>
        );
    };

    return (
        <>
            <div className="ms-Grid-row">
                <div className={clsx('ms-Grid-col', 'ms-sm6')}>
                    <TextField
                        disabled={props.readonly || props.isFullDeviceEligible}
                        label={t('email')}
                        name="email"
                        onChange={handleAddressChange}
                        errorMessage={props.validation.email}
                        required={true}
                        validateOnFocusOut={true}
                        value={addressState.email}
                        ariaLabel={t('email')}
                        onRenderLabel={(props) => renderLabels(props, "info", t('valid_email_characters'), true)} />
                </div>
                <div className={clsx('ms-Grid-col', 'ms-sm3')} >
                    <TextField
                        name="firstName"
                        disabled={props.readonly || props.isFullDeviceEligible}
                        required={true}
                        label={props.isEnterpriseCustomer === true ? t('first_name_org_label') : t('first_name')}
                        errorMessage={props.validation.firstName}
                        onChange={handleAddressChange}
                        value={addressState.firstName}
                        ariaLabel={props.isEnterpriseCustomer === true ? t('first_name_org_label') : t('first_name')}
                        maxLength={MAX_CHARACTER_LIMIT}
                        onRenderLabel={(props) => renderLabels(props, "info", "", true)}
                    />
                </div>
                <div className={clsx('ms-Grid-col', 'ms-sm3')}>
                    <TextField
                        name="lastName"
                        disabled={props.readonly || props.isFullDeviceEligible}
                        required={true}
                        label={props.isEnterpriseCustomer === true ? t('last_name_org_label') : t('last_name')}
                        errorMessage={props.validation.lastName}
                        onChange={handleAddressChange}
                        value={addressState.lastName}
                        ariaLabel={props.isEnterpriseCustomer === true ? t('last_name_org_label') : t('last_name')}
                        maxLength={MAX_CHARACTER_LIMIT}
                        onRenderLabel={(props) => renderLabels(props, "info", "", true)}
                    />

                    {/* <Icon iconName="info"
                        title={t('contact_icon_message', { fieldName: props.isEnterpriseCustomer === true ? t('last_name_org_label') : t('last_name') })}
                        className={
                            props.readonly || props.isFullDeviceEligible ? clsx(Styles.fieldsIcon, Styles.fieldIconsDisabled) :
                                props.validation?.lastName ? clsx(Styles.fieldsIcon, Styles.nameFieldsIconAfterValidation) : Styles.fieldsIcon} /> */}

                </div>
            </div>

            <div className="ms-Grid-row">
                <div className={clsx('ms-Grid-col', 'ms-sm6')}>
                    <TextField
                        ariaLabel={t('phone_number')}
                        disabled={props.isWalkInCenter || props.readonly || props.isFullDeviceEligible}
                        label={t('phone_number')}
                        name="phoneNumber"
                        onChange={handleAddressChange}
                        errorMessage={props.validation.phoneNumber}
                        required={true}
                        validateOnFocusOut={true}
                        value={addressState.phoneNumber}
                    />
                </div>
                <div className={clsx('ms-Grid-col', 'ms-sm6')}>
                    <TextField
                        name="address1"
                        required={true}
                        label={t('address_1')}
                        errorMessage={props.validation.address1}
                        disabled={props.isWalkInCenter || props.disabled || props.readonly || props.isFullDeviceEligible}
                        onChange={handleAddressChange}
                        value={addressState.address1}
                        maxLength={MAX_CHARACTER_LIMIT}
                        ariaLabel={t('address_1')}
                        onRenderLabel={(props) => renderLabels(props, "info", t('contact_icon_message', { fieldName: t('address_1') }), true)}
                    />
                </div>
            </div>

            <div className="ms-Grid-row">
                <div className={clsx('ms-Grid-col', 'ms-sm6')}>
                    <ComboBox
                        ariaLabel={t('language')}
                        autoComplete="on"
                        disabled={props.isWalkInCenter || props.readonly || props.isFullDeviceEligible}
                        label={t('language')}
                        onChange={languageOnChange}
                        options={props.languageOptions}
                        selectedKey={addressState.language}
                        styles={dropdownStyles}
                        tabIndex={props.readonly ? -1 : undefined}
                    />
                </div>
                <div className={clsx('ms-Grid-col', 'ms-sm6')}>
                    <TextField name="address2"
                        label={t('address_2')}
                        disabled={props.isWalkInCenter || props.disabled || props.readonly || props.isFullDeviceEligible}
                        onChange={handleAddressChange}
                        value={addressState.address2}
                        ariaLabel={t('address_2')}
                        maxLength={MAX_CHARACTER_LIMIT}
                        errorMessage={props.validation.address2}
                        onRenderLabel={(props) => renderLabels(props, "info", t('contact_icon_message', { fieldName: t('address_2') }), false)} />

                    {/* <Icon iconName="info"
                        title={t('contact_icon_message', { fieldName: t('address_2') })}
                        className={
                            props.isWalkInCenter || props.disabled || props.readonly || props.isFullDeviceEligible ? clsx(Styles.fieldsIcon, Styles.secondaddressFieldsIcon, Styles.fieldIconsDisabled) :
                                props.validation?.address2 ? clsx(Styles.fieldsIcon, Styles.secondaddressFieldsIconAfterValidation) : clsx(Styles.fieldsIcon, Styles.secondaddressFieldsIcon)} /> */}

                </div>
            </div>

            <div className="ms-Grid-row">
                <div className={clsx('ms-Grid-col', 'ms-sm6')}>
                    <ComboBox
                        ariaLabel={t('ship_to_country')}
                        autoComplete="on"
                        disabled={props.isCountryEnable || props.isFullDeviceEligible}
                        errorMessage={props.validation.country}
                        label={t('ship_to_country')}
                        onChange={countryOnChange}
                        options={props.countryOptions}
                        required={true}
                        selectedKey={addressState.country}
                        styles={dropdownStyles}
                        tabIndex={props.readonly ? -1 : undefined}
                    />
                </div>
                <div className={clsx('ms-Grid-col', 'ms-sm3')}>
                    <TextField
                        name="city"
                        required={true}
                        label={t('city')}
                        errorMessage={props.validation.city}
                        disabled={props.isWalkInCenter || props.disabled || props.readonly || props.isFullDeviceEligible}
                        onChange={handleAddressChange}
                        value={addressState.city}
                        ariaLabel={t('city')}
                        maxLength={MAX_CHARACTER_LIMIT} 
                        onRenderLabel={(props) => renderLabels(props, "info", t('contact_icon_message', { fieldName: t('city') }), true)} />
                </div>
                <div className={clsx('ms-Grid-col', 'ms-sm3')}>
                    <ComboBox
                        ariaLabel={t('state_province')}
                        autoComplete="on"
                        disabled={props.isWalkInCenter || props.readonly || props.isFullDeviceEligible}
                        errorMessage={props.validation.stateOrProvince}
                        label={t('state_province')}
                        onChange={provinceOnChange}
                        options={props.provincesOptions}
                        required={provinceValidation[props.country.toLowerCase()]?.required}
                        selectedKey={addressState.stateOrProvince}
                        styles={dropdownStyles}
                        tabIndex={props.readonly ? -1 : undefined}
                    />
                </div>
            </div>

            <div className="ms-Grid-row">
                <div className={clsx('ms-Grid-col', 'ms-sm6')}>
                    <TextField
                        name="postalCode"
                        label={t('zip_code')}
                        required={postalCodeValidation[props.country.toLowerCase()]}
                        errorMessage={props.validation.postalCode}
                        disabled={props.isWalkInCenter || props.disabled || props.readonly || props.isFullDeviceEligible}
                        onChange={handleAddressChange}
                        value={addressState.postalCode}
                        type="text"
                        ariaLabel={t('zip_code')} />
                </div>
                { !props.secondSRCreation &&
                    <div className={clsx('ms-Grid-col', 'ms-sm3')}>
                        <TextField
                            name="vatId"
                            label={t('vat_id')}
                            disabled={props.isWalkInCenter || props.disabled || props.readonly}
                            required={false}
                            onChange={handleAddressChange}
                            value={addressState.vatId}
                            ariaLabel={t('vat_id')}
                            maxLength={MAX_CHARACTER_LIMIT}
                            errorMessage={props.validation.vatId}
                            onRenderLabel={(labelProps) => (
                                <span className={labelProps?.disabled ? clsx(Styles.problemDescLabel, Styles.problemDescLabelDisabled) : Styles.problemDescLabel}>
                                    <span>{labelProps?.label}</span>
                                    {isVatIDRequired(userAgentTier,addressState.country) && <span className={Styles.problemDescRequired}> *</span>}
                                </span>
                            )}
                        />
                    </div>
                }
                { !props.secondSRCreation &&
                    <div className={clsx('ms-Grid-col', 'ms-sm3')}>
                        <TextField
                            name="customerPO"
                            label={t('customer_po')}
                            disabled={props.isWalkInCenter || props.disabled || props.readonly}
                            onChange={handleAddressChange}
                            value={addressState.customerPO}
                            ariaLabel={t('customer_po')}
                            maxLength={MAX_CHARACTER_LIMIT}
                        />
                    </div>
                }
            </div>
            
            { props.isOnSiteRepair ?
                <div className="ms-Grid-row">
                    <div className={clsx('ms-Grid-col', 'ms-sm6')}>
                        <TextField
                            ariaLabel={t('secondary_contact_name')}
                            label={t('secondary_contact_name')}
                            name="secondaryContactName"
                            onChange={handleAddressChange}
                            errorMessage={props.validation.secondaryContactName}
                            required={false}
                            validateOnFocusOut={true}
                            value={addressState.secondaryContactName}
                            disabled={props.readonly}
                        />
                    </div>
                    <div className={clsx('ms-Grid-col', 'ms-sm6')}>
                        <TextField
                            ariaLabel={t('secondary_contact_phone_number')}
                            label={t('secondary_contact_phone_number')}
                            name="secondaryContactPhoneNumber"
                            onChange={handleAddressChange}
                            errorMessage={props.validation.secondaryContactPhoneNumber}
                            required={false}
                            validateOnFocusOut={true}
                            value={addressState.secondaryContactPhoneNumber}
                            disabled={props.readonly}
                        />
                    </div>
                </div>
            : ''}
        </>
    );
}

